'use client';

import { createContext, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

// PD-34298 follow-up (Devin): dynamic-import the LoginModal so its bundle
// (Vimeo helpers, MUI internals, design-system snackbar, react-query
// QueryClientProvider, etc.) only loads when a user actually triggers a
// login flow. The provider mounts unconditionally in `ProviderWrapper`
// at the app root, so a static import here would defeat the lazy-load
// strategy used everywhere else in the app shell. ssr: false is safe —
// LoginModal is interaction-only (no SSR'd content to hydrate).
const LoginModal = dynamic(
  () => import('@startengine/design-system.ui.login-modal').then((m) => m.LoginModal),
  { ssr: false },
);

/**
 * Single shared LoginModal context for the whole app tree.
 *
 * Why this exists (PD-34298):
 *
 * The previous design was that every component calling `useLoginModal()` rendered
 * its own `<LoginModal>` element. On a typical Builder.io campaign page, 8-10
 * sibling components (TopNav, Hero, CTAs, Perks, Discussion, Footer, sidebar,
 * floating bubble, …) would each mount their own LoginModal instance, each
 * running its own `useBodyScrollLock` effect that mutates `document.body.style`.
 *
 * That created two failure modes:
 *
 * 1. **Body-style thrashing.** All 10 instances raced each other (and a 100ms
 *    failsafe timer) to set/clear `body.style.overflow`, `position`, `top`,
 *    `padding-right`. Even when the visible behavior was OK on Chromium, mobile
 *    Safari would flicker due to the conflicting style writes during animation.
 *
 * 2. **The mount-loop bug.** When a `useLoginModal()` caller rendered its
 *    `{renderLoginModal({})}` *inside* a conditional branch — e.g. inside
 *    `{isSticky && (…)}` in `BuilderClassicSectionNav` — clicking Login set
 *    `loginModalOpen=true`, the modal mounted, `useBodyScrollLock` made body
 *    `position: fixed`, the resulting scroll event flipped `isSticky` to false,
 *    the conditional branch unmounted (taking the LoginModal with it),
 *    `useBodyScrollLock` cleanup restored scroll, that scroll event flipped
 *    `isSticky` back to true, the branch remounted with `loginModalOpen` still
 *    true, and the cycle repeated. Visible as: nav and modal flashing, page
 *    drifting upward, only fixable by full refresh.
 *
 * Hoisting the modal to a single provider mounted in the app shell eliminates
 * both failure modes by construction:
 *
 *   - Only one `useBodyScrollLock` is ever active. Nothing can race it.
 *   - The modal's mount lifecycle is tied to the provider (never unmounts
 *     during normal page interaction), so no caller's conditional branch can
 *     yank it out from under itself mid-open.
 *
 * Caller-side API is preserved via `useLoginModal()` returning the same
 * `[renderLoginModal, setLoginModalOpen, isOpen]` 3-tuple as before. The
 * `renderLoginModal({...})` call now registers caller-supplied config (offering,
 * onClose, onSuccessfulLogin, etc.) with this provider instead of returning a
 * full `<LoginModal>` element. See `src/hooks/useLoginModal.jsx`.
 */
const LoginModalContext = createContext(null);

const LoginModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Modal-config state (offering, onClose, onSuccessfulLogin, etc.) is held in
  // a ref + state combo. The state copy is what actually drives the rendered
  // <LoginModal> props; the ref is kept in sync so callers that open via
  // `setLoginModalOpen(true)` get the latest config they registered.
  const configRef = useRef({});
  const [renderedConfig, setRenderedConfig] = useState({});
  // PD-34298 follow-up (Devin): gate the actual <LoginModal> render until a
  // caller opens the modal at least once. Two reasons:
  //   1. Mounting <LoginModal {...{}} open={false}> on every page would pass
  //      undefined for `fingerprint`, `gatewayUrl`, and the handler props —
  //      design-system components with mount-time effects (gateway init,
  //      query-client setup) could mis-fire on those undefineds.
  //   2. Combined with the dynamic() import above, this defers loading the
  //      LoginModal chunk until the first user interaction needs it. Once
  //      mounted we leave it mounted (don't reset on close) so subsequent
  //      opens are instant — only the first open pays the chunk-fetch cost.
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const openLoginModal = useCallback((nextConfig) => {
    const safeConfig = nextConfig || {};
    configRef.current = safeConfig;
    setRenderedConfig(safeConfig);
    setIsOpen(true);
    setHasBeenOpened(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsOpen(false);
    // Intentionally do NOT clear configRef / renderedConfig / hasBeenOpened
    // here. Leaving the config in place lets the modal's exit transition
    // complete with the same handler set, and lets a follow-up
    // `setLoginModalOpen(true)` from the same caller open immediately without
    // a re-register round-trip or another chunk-fetch.
  }, []);

  const ctxValue = useMemo(
    () => ({ openLoginModal, closeLoginModal, isOpen }),
    [openLoginModal, closeLoginModal, isOpen],
  );

  return (
    <LoginModalContext.Provider value={ctxValue}>
      {children}
      {hasBeenOpened && <LoginModal {...renderedConfig} open={isOpen} />}
    </LoginModalContext.Provider>
  );
};

LoginModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { LoginModalContext };
export default LoginModalProvider;
